import React from 'react';
import { Box, Typography, LinearProgress, Chip, useTheme } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

interface RiskMeterProps {
  score: number;
  showDetails?: boolean;
}

const RiskMeter: React.FC<RiskMeterProps> = ({ score, showDetails = true }) => {
  const theme = useTheme();
  const getRiskLevel = () => {
    if (score >= 80) return { level: 'CRITICAL', color: 'error', icon: <ErrorIcon /> };
    if (score >= 60) return { level: 'HIGH', color: 'warning', icon: <WarningIcon /> };
    if (score >= 40) return { level: 'MEDIUM', color: 'info', icon: <InfoIcon /> };
    if (score >= 20) return { level: 'LOW', color: 'success', icon: <CheckIcon /> };
    return { level: 'MINIMAL', color: 'success', icon: <CheckIcon /> };
  };

  const risk = getRiskLevel();
  
  // Create gradient color based on score
  const getGradientColor = () => {
    if (score >= 80) return 'linear-gradient(90deg, #ff4444 0%, #cc0000 100%)';
    if (score >= 60) return 'linear-gradient(90deg, #ff9800 0%, #f57c00 100%)';
    if (score >= 40) return 'linear-gradient(90deg, #2196f3 0%, #1976d2 100%)';
    return 'linear-gradient(90deg, #4caf50 0%, #388e3c 100%)';
  };

  // Create a semi-circle gauge
  const rotation = (score / 100) * 180 - 90;

  return (
    <Box sx={{ textAlign: 'center', p: 2 }}>
      {/* Gauge Meter */}
      <Box sx={{ position: 'relative', width: 250, height: 125, mx: 'auto', mb: 3 }}>
        {/* Background Arc */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '200%',
            borderRadius: '250px 250px 0 0',
            background: 'linear-gradient(90deg, #4caf50 0%, #ffeb3b 25%, #ff9800 50%, #f44336 75%, #d32f2f 100%)',
            opacity: 0.2,
          }}
        />
        
        {/* Needle */}
        <Box
          sx={{
            position: 'absolute',
            width: '2px',
            height: '100px',
            backgroundColor: '#333',
            bottom: 0,
            left: '50%',
            transformOrigin: 'bottom',
            transform: `translateX(-50%) rotate(${rotation}deg)`,
            transition: 'transform 1s ease-in-out',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderBottom: '15px solid #333',
            },
          }}
        />
        
        {/* Center Circle */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -10,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: '#333',
            border: '2px solid white',
            zIndex: 1,
          }}
        />
        
        {/* Score Display */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
          }}
        >
          <Typography variant="h3" fontWeight="bold">
            {score.toFixed(0)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Risk Score
          </Typography>
        </Box>
        
        {/* Labels */}
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            color: theme.palette.success.main,
            fontWeight: 'bold',
          }}
        >
          0
        </Typography>
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            color: theme.palette.error.main,
            fontWeight: 'bold',
          }}
        >
          100
        </Typography>
      </Box>

      {/* Risk Level Chip */}
      <Chip
        icon={risk.icon}
        label={`${risk.level} RISK`}
        color={risk.color as any}
        sx={{ mb: 2, fontWeight: 'bold' }}
      />

      {/* Details */}
      {showDetails && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Risk Assessment
          </Typography>
          
          {/* Risk Categories */}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption">Sanctions Risk</Typography>
              <Typography variant="caption">{(score * 0.3).toFixed(0)}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={score * 0.3}
              sx={{ height: 6, borderRadius: 3, mb: 1 }}
              color={score * 0.3 > 50 ? 'error' : 'primary'}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption">License Risk</Typography>
              <Typography variant="caption">{(score * 0.2).toFixed(0)}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={score * 0.2}
              sx={{ height: 6, borderRadius: 3, mb: 1 }}
              color={score * 0.2 > 50 ? 'warning' : 'primary'}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption">Dual-Use Risk</Typography>
              <Typography variant="caption">{(score * 0.2).toFixed(0)}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={score * 0.2}
              sx={{ height: 6, borderRadius: 3, mb: 1 }}
              color={score * 0.2 > 50 ? 'warning' : 'primary'}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption">AML Risk</Typography>
              <Typography variant="caption">{(score * 0.2).toFixed(0)}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={score * 0.2}
              sx={{ height: 6, borderRadius: 3, mb: 1 }}
              color={score * 0.2 > 50 ? 'error' : 'primary'}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption">Safety Risk</Typography>
              <Typography variant="caption">{(score * 0.1).toFixed(0)}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={score * 0.1}
              sx={{ height: 6, borderRadius: 3 }}
              color="info"
            />
          </Box>

          {/* Risk Description */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2">
              {score >= 80 && 'Critical compliance issues detected. Immediate action required to avoid severe penalties and legal consequences.'}
              {score >= 60 && score < 80 && 'High risk level detected. Review compliance requirements and implement mitigation measures promptly.'}
              {score >= 40 && score < 60 && 'Medium risk level. Some compliance concerns identified that require attention and monitoring.'}
              {score >= 20 && score < 40 && 'Low risk level. Minor compliance considerations that should be addressed for best practices.'}
              {score < 20 && 'Minimal risk. Transaction appears compliant with all major regulations.'}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RiskMeter;